/** Per-device preferences. Storage can be unavailable (private mode); never throw. */

import type { Direction } from "./protocol.ts";

export type Layout = "interleaved" | "columns";

export type Prefs = {
  direction: Direction;
  source: string;
  target: string;
  /** Optional second attendee language ("" = one language, the default). */
  secondTarget: string;
  /** Seconds listeners' phones buffer speech before playing it (fewer silent gaps). */
  listenerDelayS: number;
  deviceId: string;
  layout: Layout;
  showSource: boolean;
  readAloud: boolean;
  textScale: number;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
};

export const DEFAULT_PREFS: Prefs = {
  direction: "one-way",
  source: "fr",
  target: "en",
  secondTarget: "",
  listenerDelayS: 3,
  deviceId: "",
  layout: "interleaved",
  showSource: true,
  readAloud: false,
  textScale: 1,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const KEY = "interligne.prefs.v2";

export function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return { ...DEFAULT_PREFS, ...parsed };
  } catch {
    return DEFAULT_PREFS;
  }
}

export function savePrefs(prefs: Prefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* storage unavailable */
  }
}

const ACCESS_KEY = "interligne.access";

/** The speaker access code, if the engine requires one. Kept on this device only. */
export function loadAccessCode(): string {
  try {
    return localStorage.getItem(ACCESS_KEY) ?? "";
  } catch {
    return "";
  }
}

export function saveAccessCode(code: string): void {
  try {
    if (code) localStorage.setItem(ACCESS_KEY, code);
    else localStorage.removeItem(ACCESS_KEY);
  } catch {
    /* storage unavailable */
  }
}
